// interfaces
import { IDineoutPaymentSuccess } from "@dineout-payment-success-ui/interfaces";
import {
    ICancelBookingBanner,
    ICard,
    ICommsTextBanner,
    ICta,
    ICTaList,
    IDealBookedSuccessBanner,
    IDealsInfoWidget,
    IGridWidget,
    ITableBookingSuccessWidget,
    ITableReservationSuccessPresentationalMeta,
    SuccessPagePLResponse,
} from "@dineout-payment-success-ui/interfaces/TableReservation";

// internal-deps
import { format, getCloudinaryUrl, isEmpty, TEAM_LINE } from "@swiggy-private/js-utils";

// utils
import { appendString } from "@dineout-payment-success-ui/utils/Utils";
import { getBookingDetails, getMetaDataFromOrderResponse, getTicketAttributes } from "./BookingSuccessHelpers";

// constants
import {
    CANCEL_BOOKING_TEXTS,
    DEAL_CATEGORY_SUB_PAGE,
    DEFAULT_SUCCESS_DATA,
    RESTAURANT_DEEPLINK,
    SUCCESS_PAGE_IMAGE_URL_MAP,
    TEXT_BANNER_DISPLAY_TYPE,
    VIEW_RESTAURANT_DETAILS,
    VIEW_VENUE_DETAILS,
    WIDGET_TYPES,
} from "@dineout-payment-success-ui/constants";
import { SwgyHeaderKeysList } from "@swiggy-private/core-types";
import { GlobalHeaderState } from "@swiggy-private/checkout-core";
import { isWebPlatform } from "@swiggy-private/core-ui";
import { isEventOrganisedByRX } from "./EventBooking";

export const fetchWidgetByType = (
    plResponse?: SuccessPagePLResponse,
    widgetTypes?: string[],
    displayType?: string,
):
    | Array<{
          card?: ITableBookingSuccessWidget;
      }>
    | undefined => {
    return plResponse?.success?.cards?.filter((card?: { card?: ITableBookingSuccessWidget }) => {
        const cardType = card?.card?.card?.["@type"];
        const cardDisplayType = card?.card?.card?.displayType;
        if (!cardType) return false;
        if (displayType && cardDisplayType) {
            return widgetTypes?.includes(cardType) && cardDisplayType === displayType;
        }
        return widgetTypes?.includes(cardType);
    });
};

export const getUpdatedImageUrl = (imageUrl: string) => {
    /* Currently the images being sent in PL are common across the new deal cart flow and the legacy RN flow.
    * During PP, there was a requirement to update the images for the new deal cart flow.
    * This is a temporary solution(to unblock XP rollout) to ensure that the updated images are shown 
    * instead of the actual images without affecting backward compatibility. 
    TODO: remove this once the new deal cart flow is fully launched and PL is updated with the new images. */
    const updatedImageUrl = SUCCESS_PAGE_IMAGE_URL_MAP[imageUrl];
    if (!isEmpty(updatedImageUrl)) {
        return updatedImageUrl;
    }
    return imageUrl;
};

const getImageIdFromGridWidgets = (
    cards: Array<{ card?: ICard }> | undefined,
    dealsInfoWidgetIndex: number,
    isBeforeDealsInfoWidget = false,
): string | undefined => {
    const imageIds = cards
        ?.filter((card: { card?: ICard }, index: number) => {
            const cardType = card?.card?.card?.["@type"];

            if (cardType === WIDGET_TYPES.GRID_WIDGET) {
                const isImageInfoLayoutCard =
                    (card as IGridWidget)?.card?.card?.gridElements?.infoWithStyle?.["@type"] ===
                    WIDGET_TYPES.IMAGE_INFO_LAYOUT_CARD;
                return (
                    dealsInfoWidgetIndex > -1 &&
                    isImageInfoLayoutCard &&
                    (isBeforeDealsInfoWidget
                        ? index < dealsInfoWidgetIndex && dealsInfoWidgetIndex <= index + 2
                        : index > dealsInfoWidgetIndex && index <= dealsInfoWidgetIndex + 2)
                );
            }
            return false;
        })
        ?.map(
            (card?: { card?: ICard }) =>
                (card as IGridWidget)?.card?.card?.gridElements?.infoWithStyle?.info?.[0]?.imageId,
        );

    // Remove any falsy values (undefined, null, empty string) from the array
    const validImageIds = imageIds?.filter((id): id is string => Boolean(id));

    // Return the first valid image ID or undefined if none found
    return validImageIds?.[0];
};

export const getSuccessPagePresentationalMeta = (
    orderRawData: IDineoutPaymentSuccess,
    isEventReservation?: boolean,
    plResponse?: SuccessPagePLResponse,
): ITableReservationSuccessPresentationalMeta => {
    const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform);
    const isWebFlow = isWebPlatform(platform);
    const metaDataJson = getMetaDataFromOrderResponse(orderRawData);

    if (isEmpty(metaDataJson)) {
        return {
            ...DEFAULT_SUCCESS_DATA,
            restaurantDetails: {
                id: "",
                name: "",
                address: "",
                fullAddress: "",
                city: "",
                locality: "",
                phoneNumber: "",
                latitude: 0,
                longitude: 0,
            },
        };
    }
    const { bill, ctx, items, storeInfo } = metaDataJson;
    const { name, locality, cityName, id, location } = storeInfo;
    const restaurantLocality = appendString(storeInfo?.landmark || "", storeInfo?.displayAreaName || locality);
    const address = `${restaurantLocality ? `${restaurantLocality},` : ""} ${cityName}`;

    const dealsInfoWidget = fetchWidgetByType(plResponse, [WIDGET_TYPES.DEALS_INFO_WIDGET]) as IDealsInfoWidget[];
    const ctaList = fetchWidgetByType(plResponse, [WIDGET_TYPES.CTA_LIST]) as ICTaList[];
    const dealBookedSuccessBanner = fetchWidgetByType(plResponse, [
        WIDGET_TYPES.DEAL_BOOKED_SUCCESS_BANNER,
    ]) as IDealBookedSuccessBanner[];

    const rewardsPoolWidget = metaDataJson?.rewardsPoolWidget;
    const calloutsBanner = metaDataJson?.carouselBanners;
    const dealsInfoWidgetIndex = plResponse?.success?.cards?.findIndex(
        (card?: { card?: ICard }) => card?.card?.card?.["@type"] === WIDGET_TYPES.DEALS_INFO_WIDGET,
    ) as number;

    const backgroundImageFromGridWidget = getImageIdFromGridWidgets(
        plResponse?.success?.cards,
        dealsInfoWidgetIndex,
        true,
    );
    const rewardsWidget = metaDataJson?.items?.[0]?.rewardsWidget;
    const whatToDoNextSrc = getImageIdFromGridWidgets(plResponse?.success?.cards, dealsInfoWidgetIndex);

    const whatToDoNextSrcUpdated = getUpdatedImageUrl(whatToDoNextSrc || "");

    const commsTextBannerProps = fetchWidgetByType(
        plResponse,
        [WIDGET_TYPES.TEXT_BANNER_V2, WIDGET_TYPES.TEXT_BANNER],
        TEXT_BANNER_DISPLAY_TYPE.DINEOUT_CHARGE_CALLOUT,
    )?.[0] as ICommsTextBanner;

    const cancelBookingBannerProps = fetchWidgetByType(
        plResponse,
        [WIDGET_TYPES.TEXT_BANNER_V2, WIDGET_TYPES.TEXT_BANNER],
        TEXT_BANNER_DISPLAY_TYPE.DINEOUT_CANCEL_RESERVATION,
    )?.[0] as ICancelBookingBanner;

    const storeInfoFromPlResponse = dealsInfoWidget?.[0]?.card?.card?.bookedDealList?.[0]?.restaurantInfo;
    const ctaListFromPlResponse = ctaList?.[0]?.card?.card?.ctas;
    const ctaSubTextFromPlResponse = ctaList?.[0]?.card?.card?.text;

    const restaurantDetails = {
        id,
        name,
        address,
        fullAddress: `${name}, ${address}`,
        city: cityName,
        locality,
        phoneNumber: storeInfoFromPlResponse?.phoneNumber,
        ...location,
    };

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { backgroundUrl, dealPurchaseStatus, text, subtext, textColor, cta, girf_logo } =
        dealBookedSuccessBanner?.[0]?.card?.card || {};

    const bookingSuccessBanner = {
        bannerImageMeta: {
            src: getUpdatedImageUrl(backgroundUrl || backgroundImageFromGridWidget || ""),
            isGridWidgetBanner: !backgroundUrl && !!backgroundImageFromGridWidget,
        },
        dealPurchaseStatus: dealPurchaseStatus || "",
        titleText: text || "",
        subtitle: subtext || "",
        dealPurchaseStatusColor: textColor || "",
        cta,
        girfLogo: girf_logo || "",
    };

    const transformedCtas = ctaListFromPlResponse?.map((cta: ICta, index: number) => {
        let variant = "primary";
        if (ctaListFromPlResponse.length > 1) {
            variant = index === 0 ? "secondary" : "primary";
        }

        return {
            text: cta?.text,
            disabled: cta?.subtext === "disabled",
            deeplink: cta?.link,
            variant,
        };
    });

    const ticketAttributes = getTicketAttributes(bill, ctx, items?.[0]);

    const viewRestaurantCtaMeta = {
        link: format(RESTAURANT_DEEPLINK, id),
        text: isEventReservation ? VIEW_VENUE_DETAILS : VIEW_RESTAURANT_DETAILS,
    };

    const isNonRxBooking = isEventReservation ? !isEventOrganisedByRX(storeInfo) : false;

    return {
        commsTextBannerProps,
        bookingSuccessBanner,
        bookingDetails: getBookingDetails(metaDataJson?.items?.[0]),
        restaurantDetails,
        ticketAttributes: {
            ...ticketAttributes,
            offerDetails: {
                ...ticketAttributes.offerDetails,
                chipSrc:
                    ticketAttributes?.offerDetails?.chipSrc ||
                    (getCloudinaryUrl(
                        dealsInfoWidget?.[0]?.card?.card?.bookedDealList?.[0]?.offerLogo?.logo || "",
                    ) as string),
            },
        },
        cancelBookingMeta: {
            show: !isEventReservation && !isEmpty(cancelBookingBannerProps),
            text: cancelBookingBannerProps?.card?.card?.action?.text || CANCEL_BOOKING_TEXTS.BUTTON_TEXT,
            leftText: cancelBookingBannerProps?.card?.card?.text?.title || CANCEL_BOOKING_TEXTS.MAIN_TEXT,
        },
        viewRestaurantCtaMeta: !isWebFlow && !isNonRxBooking ? viewRestaurantCtaMeta : undefined,
        ctaSubTextMeta: !isWebFlow ? ctaSubTextFromPlResponse : undefined,
        ctas: !isWebFlow ? transformedCtas : undefined,
        whatToDoNextSrc: !isWebFlow
            ? getCloudinaryUrl(whatToDoNextSrcUpdated, {}, { teamLine: TEAM_LINE.DINEOUT })
            : undefined,
        rewardsWidget,
        rewardsPoolWidget,
        calloutsBanner,
    };
};

export const getSubPage = (dealCategory: string) => {
    return DEAL_CATEGORY_SUB_PAGE[dealCategory] || "";
};
